import { useEffect, useState } from "react";
import axios from "axios";
import { FiSearch, FiX } from "react-icons/fi";
import { Link } from "react-router-dom";

type Editorial = {
  id: number;
  judul: string;
  gambar: string;
  created_at: string;
};

const API = process.env.REACT_APP_API_BASE_URL;

interface Props {
  open: boolean;
  onClose: () => void;
}

const SearchPopup = ({ open, onClose }: Props) => {
  const [keyword, setKeyword] = useState("");
  const [data, setData] = useState<Editorial[]>([]);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);

  // ==============================
  // DEFAULT NEWS (SAAT POPUP DIBUKA)
  // ==============================

  const fetchDefault = async () => {
    try {
      const res = await axios.get(`${API}/api/editorial?per_page=4`);

      setData(res.data.data);
      setLastPage(res.data.last_page || 1);
    } catch (err) {
      console.log(err);
    }
  };

  // ==============================
  // SEARCH NEWS
  // ==============================

  const fetchSearch = async () => {
    try {
      const res = await axios.get(`${API}/api/editorial?search=${keyword}&per_page=4&page=${page}`);

      setData(res.data.data);
      setLastPage(res.data.last_page || 1);
    } catch (err) {
      console.log(err);
    }
  };

  // ==============================
  // LOAD DEFAULT SAAT POPUP DIBUKA
  // ==============================

  useEffect(() => {
    if (open) {
      fetchDefault();
    }
  }, [open]);

  // ==============================
  // SEARCH DENGAN DEBOUNCE
  // ==============================

  useEffect(() => {
    const delay = setTimeout(() => {
      if (keyword.length > 2) {
        fetchSearch();
      }

      if (keyword.length === 0) {
        fetchDefault();
      }
    }, 400);

    return () => clearTimeout(delay);
  }, [keyword, page]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex justify-center items-start pt-14 px-4">
      <div className="bg-white w-full max-w-2xl rounded-xl shadow-2xl p-6 relative">
        {/* CLOSE */}
        <button onClick={onClose} className="absolute right-4 top-4 text-gray-500 hover:text-black">
          <FiX size={24} />
        </button>

        {/* SEARCH INPUT */}
        <div className="flex items-center border rounded-lg px-4 py-3 mt-6 mb-6">
          <FiSearch className="text-xl mr-2 text-gray-400" />

          <input
            type="text"
            placeholder="Cari Isu/Informasi..."
            value={keyword}
            onChange={(e) => {
              setKeyword(e.target.value);
              setPage(1);
            }}
            className="w-full outline-none"
          />
        </div>

        {/* RESULT LIST */}
        <div className="space-y-4 max-h-[400px] overflow-y-auto">
          {data.map((item) => (
            <Link key={item.id} to={`/berita/${item.id}`} onClick={onClose} className="flex gap-3 group">
              <img src={`${item.gambar}`} alt="" className="w-[90px] h-[70px] object-cover rounded" />

              <div>
                <p className="font-medium group-hover:text-red-600 leading-snug">{item.judul}</p>

                <span className="text-xs text-gray-500">
                  {new Date(item.created_at).toLocaleDateString("id-ID", {
                    day: "2-digit",
                    month: "long",
                    year: "numeric",
                  })}
                </span>
              </div>
            </Link>
          ))}

          {/* NOT FOUND */}
          {keyword.length > 2 && data.length === 0 && <p className="text-center text-gray-500">Berita tidak ditemukan</p>}
        </div>

        {/* PAGINATION */}
        {lastPage > 1 && (
          <div className="flex justify-center gap-2 mt-6 flex-wrap">
            {[...Array(lastPage)].map((_, i) => (
              <button key={i} onClick={() => setPage(i + 1)} className={`px-3 py-1 rounded-md text-sm ${page === i + 1 ? "bg-red-600 text-white" : "bg-gray-200 hover:bg-gray-300"}`}>
                {i + 1}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchPopup;
